import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { useState, useEffect, useRef, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  Menu,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import { teamsAction } from "redux/slice/teams";
import { useNavigate } from "react-router-dom";
import Loader from "utils/Loader";
import EditIcon from "@mui/icons-material/Edit";
import { checkPermission } from "utils/HelperFunctions";
import {
  PERMISSIONS_NAME,
  TEAM_MEMBER,
  UNASSIGNED,
  PENDING_CONFIRMATION,
  PENDING_USERS,
  INVITE_SENT,
} from "lib/constants";
import { Circle, DeleteOutline, DriveFileRenameOutline } from "@mui/icons-material";
import Swal from "sweetalert2";
import { enqueueSnackbar } from "notistack";
import { deleteAPICall, putAPICall } from "lib/callApi";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import { resendInvitation } from "apis/pendingUsers";
import MDTypography from "components/MDTypography";
import AddTeamMemberModal from "./AddTeamMemberModal";

function TeamMemberRow({
  row,
  user,
  handleEditUser,
  menuDropdown,
  setMenuDropdown,
  handleTeamMemberDelete,
  handleResendInvitation,
  handleAllowPendingUserOpen,
  handleSeatCountCheck,
  handleDenyUser,
}) {
  const buttonRef = useRef(null);

  const showSubMenu = useCallback(
    (userId) => {
      setMenuDropdown(userId);
    },
    [setMenuDropdown]
  );

  const closeSubMenu = useCallback(() => {
    setMenuDropdown(null);
  }, [setMenuDropdown]);

  return (
    <TableRow
      key={row.userId}
      hover
      sx={{
        cursor: "pointer",
        ...(row.disabled
          ? {
              "& .MuiTableCell-root": {
                color: "grey",
              },
            }
          : {}),
      }}
      onClick={() => handleEditUser(row)}
    >
      <TableCell>{row.teamName || UNASSIGNED}</TableCell>
      <TableCell sx={{ textTransform: "capitalize" }}>{row.permissionProfile}</TableCell>
      <TableCell>{row.memberName}</TableCell>
      <TableCell>{row.role ? row.role : "-"}</TableCell>
      <TableCell>{row.email}</TableCell>
      <TableCell>
        {[PENDING_USERS, INVITE_SENT].includes(row.status) ? (
          <MDBox
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
            }}
          >
            <Circle
              fontSize="large"
              sx={{
                color: row.status === INVITE_SENT && !row.invitationExpired ? "yellow" : "red",
              }}
            />
            {row.status === INVITE_SENT ? (
              <MDBox
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "start",
                }}
              >
                <MDTypography variant="body2" color="black" ml={2}>
                  {row.invitationExpired ? "Invite Expired" : "Invite Pending"}
                </MDTypography>
                {row.invitationExpired ? (
                  <MDTypography
                    variant="caption"
                    color="black"
                    ml={2}
                    sx={{ alignSelf: "center" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResendInvitation(row);
                    }}
                  >
                    Resend
                  </MDTypography>
                ) : (
                  <MDTypography variant="caption" color="black" ml={2}>
                    {`(${row.invitationExpiresIn})`}
                  </MDTypography>
                )}
              </MDBox>
            ) : (
              <MDTypography variant="body2" color="black" ml={2}>
                Requesting Access
              </MDTypography>
            )}
          </MDBox>
        ) : null}
      </TableCell>
      {(user.type === "company" ||
        checkPermission(PERMISSIONS_NAME.MANAGE_WORKSPACE, user.userPermission)) && (
        <TableCell align="right" onClick={(e) => e?.stopPropagation()}>
          <MDBox sx={{ display: "flex", justifyContent: "end", alignItems: "center" }}>
            {row.status === PENDING_CONFIRMATION ? (
              <>
                <MDButton
                  variant="gradient"
                  color="success"
                  size="small"
                  onClick={(e) => {
                    e?.stopPropagation();
                    handleSeatCountCheck(() => handleAllowPendingUserOpen(row.userId));
                  }}
                >
                  Allow access
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="error"
                  size="small"
                  sx={{ ml: 2 }}
                  onClick={(e) => {
                    e?.stopPropagation();
                    handleDenyUser(row);
                  }}
                >
                  Deny
                </MDButton>
              </>
            ) : null}
            <IconButton
              size="small"
              disableRipple
              color="inherit"
              aria-controls="team-members-menu"
              aria-haspopup="true"
              variant="contained"
              id={`team-members-menu-icon-${row.userId}`}
              ref={buttonRef}
              sx={{ ml: 4 }}
              onClick={(e) => {
                e?.stopPropagation();
                showSubMenu(row.userId);
              }}
            >
              <Icon fontSize="medium" id={`team-members-menu-icon-${row.userId}`}>
                menu
              </Icon>
            </IconButton>
            <Menu
              anchorEl={buttonRef.current}
              open={menuDropdown === row.userId}
              onClose={closeSubMenu}
            >
              <MenuItem
                onClick={() => handleEditUser(row)}
                key={`team-members-menu-edit-icon-${row.userId}`}
              >
                <MDBox sx={{ display: "flex", alignItems: "center" }}>
                  <IconButton
                    size="small"
                    disableRipple
                    color="inherit"
                    aria-controls="team-members-menu"
                    aria-haspopup="true"
                    variant="contained"
                    id={`team-members-menu-edit-icon-${row.userId}`}
                  >
                    <EditIcon fontSize="medium" id={`team-members-menu-edit-icon-${row.userId}`} />
                  </IconButton>
                  Edit
                </MDBox>
              </MenuItem>
              <MenuItem
                onClick={() => handleTeamMemberDelete(row.userId, row.memberName)}
                key={`team-members-menu-delete-icon-${row.userId}`}
              >
                <MDBox sx={{ display: "flex", alignItems: "center" }}>
                  <IconButton
                    size="small"
                    disableRipple
                    color="inherit"
                    aria-controls="team-members-menu"
                    aria-haspopup="true"
                    variant="contained"
                    id={`team-members-menu-delete-icon-${row.userId}`}
                  >
                    <DeleteOutline
                      fontSize="medium"
                      id={`team-members-menu-delete-icon-${row.userId}`}
                    />
                  </IconButton>
                  Delete
                </MDBox>
              </MenuItem>
              {row.status === INVITE_SENT ? (
                <MenuItem
                  onClick={() => {
                    setMenuDropdown(null);
                    handleResendInvitation(row);
                  }}
                  key={`team-members-menu-resend-invite-icon-${row.userId}`}
                >
                  <MDBox sx={{ display: "flex", alignItems: "center" }}>
                    <IconButton
                      size="small"
                      disableRipple
                      color="inherit"
                      aria-controls="team-members-menu"
                      aria-haspopup="true"
                      variant="contained"
                      id={`team-members-menu-resend-invite-icon-${row.userId}`}
                    >
                      <DriveFileRenameOutline
                        fontSize="medium"
                        id={`team-members-menu-resend-invite-icon-${row.userId}`}
                      />
                    </IconButton>
                    Resend invite
                  </MDBox>
                </MenuItem>
              ) : null}
            </Menu>
          </MDBox>
        </TableCell>
      )}
    </TableRow>
  );
}

function TeamMembersTable({ teamId, setSelectedTeamId, handleSeatCountCheck }) {
  const theme = useTheme();
  if (teamId === UNASSIGNED) teamId = null;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const team = useSelector((state) => state.teams.team);
  const totalTeamMember = useSelector((state) => state.teams.totalMembers);
  const teamLoading = useSelector((state) => state.teams.teamLoading);
  const user = useSelector((state) => state.loginUser.loginUserState);
  const [userToEdit, setUserToEdit] = useState();
  const [menuDropdown, setMenuDropdown] = useState(false);
  const [allowPendingUserOpen, setAllowPendingUserOpen] = useState(false);
  const [allowPendingUserID, setAllowPendingUserID] = useState();
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const [teamPayload, setTeamPayload] = useState({
    searchBy: "",
    pageSize: 10,
    pageNumber: 0,
    timezone: moment.tz.guess(),
  });
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setUserToEdit();
  };

  useEffect(() => {
    dispatch(teamsAction.getTeamSaga({ ...teamPayload, id: teamId, navigateFunction: navigate }));
  }, [teamPayload]);

  const handlePageChange = (_, newPage) => {
    setTeamPayload((prevPayload) => ({ ...prevPayload, pageNumber: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    setTeamPayload((prevPayload) => ({
      ...prevPayload,
      pageSize: parseInt(event.target.value, 10),
    }));
  };

  const handleTeamMemberDelete = async (teamMemberId, name) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${name}? This will delete all data for this user from the system.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: theme.palette.primaryRed.main,
      cancelButtonColor: theme.palette.gray[80],
      confirmButtonText: "Yes, delete it!",
    })
      .then(async (result) => {
        if (result.isConfirmed) {
          const resultResponse = await deleteAPICall(`${TEAM_MEMBER}/${teamMemberId}`, true, null);
          try {
            if (resultResponse?.data?.success) {
              enqueueSnackbar(
                JSON.stringify({
                  icon: "success",
                  title: "Deleted",
                  text: "Team member successfully deleted.",
                }),
                {
                  variant: "custom",
                }
              );
              dispatch(
                teamsAction.getTeamSaga({
                  pageNumber: teamPayload.pageNumber,
                  pageSize: 10,
                  id: teamId,
                })
              );
            } else {
              enqueueSnackbar(
                JSON.stringify({
                  icon: "error",
                  title: "Error",
                  text:
                    resultResponse?.data?.message ||
                    resultResponse?.response?.data?.message ||
                    "Something Went Wrong",
                }),
                {
                  variant: "custom",
                }
              );
              dispatch(
                teamsAction.getTeamSaga({
                  pageNumber: teamPayload.pageNumber,
                  pageSize: 10,
                  id: teamId,
                })
              );
            }
          } catch (error) {
            enqueueSnackbar(
              JSON.stringify({
                icon: "error",
                title: "Error",
                text: error.response.data.message || error.message || "Something went wrong",
              }),
              {
                variant: "custom",
              }
            );
          }
        }
      })
      .catch((error) => {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: error.response.data.message,
          }),
          {
            variant: "custom",
          }
        );
        dispatch(
          teamsAction.getTeamSaga({
            pageNumber: teamPayload.pageNumber,
            pageSize: 10,
            id: teamId,
          })
        );
      });
  };

  const handleEditUser = (row) => {
    setUserToEdit(row.userId);
    setOpen(true);
  };

  const handleResendInvitation = (data) => {
    if (!data.userId) return;
    setLoading(true);
    Swal.fire({
      title: "Are you sure?",
      text: `Are you sure you want to resend invitation?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Resend!",
    })
      .then(async (result) => {
        if (result.isConfirmed) {
          try {
            const resendInvitationResponse = await resendInvitation(data.userId);
            if (resendInvitationResponse?.data?.success) {
              dispatch(
                teamsAction.getTeamSaga({ ...teamPayload, id: teamId, navigateFunction: navigate })
              );
              enqueueSnackbar(
                JSON.stringify({
                  icon: "success",
                  title: "Sent",
                  text: "Invitation resend successfully.",
                }),
                {
                  variant: "custom",
                }
              );
              setLoading(false);
            } else {
              setLoading(false);
              enqueueSnackbar(
                JSON.stringify({
                  icon: "error",
                  title: "Error",
                  text: resendInvitationResponse?.data?.message || "Something Went Wrong",
                }),
                {
                  variant: "custom",
                }
              );
              setLoading(false);
            }
          } catch (error) {
            setLoading(false);
            enqueueSnackbar(
              JSON.stringify({
                icon: "error",
                title: "Error",
                text: error.response.data.message || error.message || "Something went wrong",
              }),
              {
                variant: "custom",
              }
            );
          }
        }
        setLoading(false);
      })
      .catch();
  };

  const handleAllowPendingUserOpen = (userId) => {
    setAllowPendingUserID(userId);
    setAllowPendingUserOpen(true);
  };

  const handleAllowPendingUserClose = () => {
    setAllowPendingUserID();
    setAllowPendingUserOpen(false);
  };

  const updatePendingUserAPIRequest = async (id, data) => {
    const apiResponse = await putAPICall(`${PENDING_USERS}/${id}`, true, data);
    if (apiResponse?.data?.success) {
      dispatch(
        teamsAction.getTeamSaga({
          pageNumber: teamPayload.pageNumber,
          pageSize: 10,
          id: teamId,
        })
      );
    }
    return apiResponse;
  };

  const handleDenyUser = async (data) => {
    if (!data.userId) return;
    Swal.fire({
      title: "Are you sure?",
      text: `Are you sure you want to reject ${data?.user?.name || "this user"} ?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Reject it!",
    })
      .then(async (result) => {
        if (result.isConfirmed) {
          try {
            const deleteTeamResponse = await updatePendingUserAPIRequest(data?.userId, {
              allowAccess: false,
            });
            if (deleteTeamResponse?.data?.success) {
              enqueueSnackbar(
                JSON.stringify({
                  icon: "success",
                  title: "Deleted",
                  text: "User rejected successfully.",
                }),
                {
                  variant: "custom",
                }
              );
              dispatch(
                teamsAction.getTeamSaga({
                  pageNumber: teamPayload.pageNumber,
                  pageSize: 10,
                  id: teamId,
                })
              );
            } else {
              enqueueSnackbar(
                JSON.stringify({
                  icon: "error",
                  title: "Error",
                  text: deleteTeamResponse?.data?.message || "Something Went Wrong",
                }),
                {
                  variant: "custom",
                }
              );
              dispatch(
                teamsAction.getTeamSaga({
                  pageNumber: teamPayload.pageNumber,
                  pageSize: 10,
                  id: teamId,
                })
              );
            }
          } catch (error) {
            enqueueSnackbar(
              JSON.stringify({
                icon: "error",
                title: "Error",
                text: error.response.data.message || error.message || "Something went wrong",
              }),
              {
                variant: "custom",
              }
            );
          }
        }
      })
      .catch();
  };

  return (
    <MDBox mt={4}>
      <MDBox
        pr={1}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          "& .MuiInputLabel-formControl": { lineHeight: "15px" },
        }}
        mt={2}
      >
        <MDInput
          label="Search employee"
          style={{ width: "18%" }}
          onChange={(event) =>
            setTeamPayload((prevState) => ({
              ...prevState,
              searchBy: event.target.value,
              pageNumber: 0,
            }))
          }
        />
        {(user?.type === "company" ||
          checkPermission(PERMISSIONS_NAME.MANAGE_USERS, user.userPermission)) && (
          <MDButton
            color="primaryRed"
            onClick={() => {
              handleSeatCountCheck(handleOpen);
            }}
          >
            + Add new user
          </MDButton>
        )}
      </MDBox>

      <IconButton
        size="large"
        disableRipple
        color="inherit"
        aria-controls="notification-menu"
        aria-haspopup="true"
        variant="contained"
        onClick={() => setSelectedTeamId(null)}
      >
        <Icon fontSize="medium">arrow_back</Icon>
      </IconButton>

      <MDBox mt={2} sx={{ position: "relative" }}>
        <Loader loading={teamLoading || loading} />
        <TableContainer component={Paper}>
          <Table aria-label="simple table">
            <TableHead sx={{ display: "table-header-group" }}>
              <TableRow>
                <TableCell>Team</TableCell>
                <TableCell>Permission Profile</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Email</TableCell>
                <TableCell />
                {(user.type === "company" ||
                  checkPermission(PERMISSIONS_NAME.MANAGE_WORKSPACE, user.userPermission)) && (
                  <TableCell />
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {team?.map((row) => (
                <TeamMemberRow
                  key={row.userCompanyLinkId}
                  row={row}
                  user={user}
                  handleEditUser={handleEditUser}
                  handleTeamMemberDelete={handleTeamMemberDelete}
                  menuDropdown={menuDropdown}
                  setMenuDropdown={setMenuDropdown}
                  handleResendInvitation={handleResendInvitation}
                  handleAllowPendingUserOpen={handleAllowPendingUserOpen}
                  handleDenyUser={handleDenyUser}
                  handleSeatCountCheck={handleSeatCountCheck}
                />
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 20, 25, 50]}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleChangeRowsPerPage}
            component="div"
            count={totalTeamMember}
            rowsPerPage={teamPayload?.pageSize}
            page={teamPayload.pageNumber}
            sx={{ flex: "1 1 auto" }}
          />
        </TableContainer>
      </MDBox>
      {open || allowPendingUserOpen ? (
        <AddTeamMemberModal
          setOpen={open ? handleClose : handleAllowPendingUserClose}
          open={open || allowPendingUserOpen}
          userToEdit={open ? userToEdit : allowPendingUserID}
          allowAccess={allowPendingUserOpen}
          updatePendingUserAPIRequest={updatePendingUserAPIRequest}
          initialState={{ isAddNew: true }}
          teamId={teamId}
        />
      ) : null}
    </MDBox>
  );
}

export default TeamMembersTable;
